<template>
  <div class="page-container">
    <div class="header-row">
      <h2 class="page-title">{{ $t('household.nomenclatureTypes.title') }}</h2>
      <button class="btn-primary" @click="openDialog()">
        <LucideIcon :size="16" class="mr-2">
          <path d="M5 12h14"/><path d="M12 5v14"/>
        </LucideIcon>
        {{ $t('household.nomenclatureTypes.addBtn') }}
      </button>
    </div>

    <!-- Таблица -->
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ $t('household.nomenclatureTypes.colName') }}</th>
            <th>{{ $t('household.nomenclatureTypes.colDescription') }}</th>
            <th class="text-right">{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in nomenclatureTypes" :key="item.id">
            <td>
              <div class="item-name">{{ item.name }}</div>
            </td>
            <td>{{ item.description || '—' }}</td>
            <td class="text-right">
              <button class="action-btn edit" @click="openDialog(item)">
                <LucideIcon :size="16">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </LucideIcon>
              </button>
              <button class="action-btn delete" @click="handleDelete(item.id)">
                <LucideIcon :size="16">
                  <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                </LucideIcon>
              </button>
            </td>
          </tr>
          <tr v-if="nomenclatureTypes.length === 0">
            <td colspan="3" class="empty-row">{{ $t('household.nomenclatureTypes.notFound') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Диалог -->
    <div v-if="isDialogOpen" class="modal-overlay" @click.self="closeDialog">
      <div class="modal-content">
        <h3 class="modal-title">
          {{ editingItem ? $t('household.nomenclatureTypes.dialogEditTitle') : $t('household.nomenclatureTypes.dialogAddTitle') }}
        </h3>
        <form @submit.prevent="handleSubmit" class="modal-form">
          <div class="form-group">
            <label>{{ $t('household.nomenclatureTypes.colName') }}</label>
            <input v-model="formData.name" type="text" required :placeholder="$t('household.nomenclatureTypes.namePlaceholder')" />
          </div>

          <div class="form-group">
            <label>{{ $t('household.nomenclatureTypes.colDescription') }}</label>
            <textarea v-model="formData.description" rows="3" :placeholder="$t('household.nomenclatureTypes.descriptionPlaceholder')"></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeDialog">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ editingItem ? $t('common.save') : $t('common.add') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import { 
  getNomenclatureTypes, 
  createNomenclatureType, 
  updateNomenclatureType, 
  deleteNomenclatureType 
} from '@/api/household_nomenclatures';

export default {
  name: 'HouseholdNomenclatureTypesView',
  components: { LucideIcon },
  data() {
    return {
      nomenclatureTypes: [],
      isDialogOpen: false,
      editingItem: null,
      formData: {
        name: '',
        description: '',
      },
    };
  },
  async created() {
    await this.loadTypes();
  },
  methods: {
    async loadTypes() {
      try {
        const res = await getNomenclatureTypes({ page_size: 100 });
        this.nomenclatureTypes = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      } catch (err) {
        console.error('Failed to load nomenclature types:', err);
      }
    },
    openDialog(item = null) {
      if (item) {
        this.editingItem = item;
        this.formData = { ...item };
      } else {
        this.editingItem = null;
        this.formData = { name: '', description: '' };
      }
      this.isDialogOpen = true;
    },
    closeDialog() {
      this.isDialogOpen = false;
    },
    async handleSubmit() {
      try {
        const payload = {
          name: this.formData.name,
          description: this.formData.description || '',
        };

        if (this.editingItem) {
          await updateNomenclatureType(this.editingItem.id, payload);
        } else {
          await createNomenclatureType(payload);
        }
        await this.loadTypes();
        this.closeDialog();
      } catch (err) {
        console.error('Submit error:', err);
        alert(this.$t('household.nomenclatureTypes.saveError'));
      }
    },
    async handleDelete(id) {
      if (confirm(this.$t('household.nomenclatureTypes.confirmDelete'))) {
        await deleteNomenclatureType(id);
        await this.loadTypes();
      }
    }
  }
};
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }

.table-container {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: #fafafa;
  text-align: left;
  padding: 1rem 1.5rem;
  color: #717182;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #f1f1f4;
}
.data-table td { padding: 1.25rem 1.5rem; border-top: 1px solid #f1f1f4; color: #030229; vertical-align: middle; }

.item-name { font-weight: 600; color: #030229; font-size: 1rem; }

.text-right { text-align: right; }
.action-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.4rem;
  margin-left: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s;
  color: #3b82f6;
}
.action-btn.delete { color: #ef4444; }
.action-btn:hover { background: #f1f5f9; }

.empty-row { text-align: center; padding: 4rem; color: #717182; font-size: 1rem; }

.btn-primary {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}
.btn-primary:hover { background: #1d4ed8; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #ffffff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 500px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-title { color: #030229; font-size: 1.5rem; font-weight: 700; margin-bottom: 2rem; }

.form-group { margin-bottom: 1.25rem; }
.form-group label { display: block; color: #4b5563; font-size: 0.875rem; font-weight: 500; margin-bottom: 0.5rem; }
.form-group input, .form-group textarea {
  width: 100%;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  color: #111827;
  outline: none;
  transition: all 0.2s;
}
.form-group input:focus, .form-group textarea:focus { border-color: #3b82f6; background: #ffffff; }

.modal-actions { display: flex; gap: 1rem; margin-top: 2rem; }
.btn-secondary {
  flex: 1;
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 0.75rem;
  padding: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-secondary:hover { background: #e5e7eb; }

.mr-2 { margin-right: 0.5rem; }
</style>
